<template>
	<div class="mini-dist" v-loading="pageLoading">
		<div v-if="!configured" class="guide-card">
			<div class="guide-title">尚未开通小程序配置</div>
			<p class="guide-desc">请先在「小程序账户」完成 AppID 与店铺名称保存后再设置分销。</p>
			<el-button type="primary" size="small" @click="goAccount">去账户设置</el-button>
		</div>

		<el-form v-else ref="form" :model="form" :rules="formRules" label-width="160px" size="small" class="setup-form">
			<div class="form-section">
				<div class="section-title">分销开关</div>
				<el-form-item label="启用分销">
					<el-switch
						v-model="form.dist_enabled"
						:active-value="1"
						:inactive-value="0"
						active-color="#ff4714"
					/>
					<span class="state-text">{{ form.dist_enabled === 1 ? '已启用' : '已停用' }}</span>
					<p class="field-tip">关闭后停止新的绑定与佣金产生；已产生的待结算佣金仍会按计划结算。</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">佣金比例</div>
				<el-form-item label="佣金占利润" prop="commission_percent">
					<el-input v-model="form.commission_percent" placeholder="0-100">
						<template slot="append">%</template>
					</el-input>
					<p class="field-tip">只影响之后新产生的佣金，存量流水不追溯。提交按千分比（50% → 500）。</p>
				</el-form-item>
				<el-form-item label="一级占比" prop="first_percent">
					<el-input v-model="form.first_percent" placeholder="0-100" @input="onFirstChange">
						<template slot="append">%</template>
					</el-input>
				</el-form-item>
				<el-form-item label="二级占比">
					<el-input v-model="form.second_percent" disabled>
						<template slot="append">%</template>
					</el-input>
					<p class="field-tip">一级 + 二级必须等于 100%。二级随一级自动补齐。二级（离单近）默认拿大头。</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">开通与结算</div>
				<el-form-item label="自动开通邀请人数" prop="dist_apply_team_count">
					<el-input v-model="form.dist_apply_team_count" placeholder="1-100">
						<template slot="append">人</template>
					</el-input>
					<p class="field-tip">分享链接带来的新用户登录满该人数后自动开通分销员。修改不影响已开通关系。</p>
				</el-form-item>
				<el-form-item label="结算等待天数" prop="dist_settle_days">
					<el-input v-model="form.dist_settle_days" placeholder="1-90">
						<template slot="append">天</template>
					</el-input>
					<p class="field-tip">订单发货满 N 天后，每日自动结算入余额。建议 7-30 天。修改即时生效，无手动结算按钮。</p>
				</el-form-item>
			</div>

			<div class="form-section">
				<div class="section-title">升级规则</div>
				<el-form-item label="自动升级">
					<el-switch v-model="form.auto_upgrade" active-color="#ff4714" />
					<p class="field-tip">每日 02:10 扫描一次，只升不降。apitest 需找后端手动触发。文案「邀请用户的签收单」指邀请来的新用户，不是已开通的下级分销员。</p>
				</el-form-item>
				<el-form-item label="匹配方式">
					<el-radio-group v-model="form.match_mode">
						<el-radio label="all">全部条件满足</el-radio>
						<el-radio label="any">任一条件满足</el-radio>
					</el-radio-group>
					<p class="field-tip">二级分销员升一级。手动把一级调回二级后，次日达标仍可能再升。</p>
				</el-form-item>
				<el-form-item label="邀请人数">
					<el-checkbox v-model="form.cond_team">启用</el-checkbox>
					<el-input
						v-model="form.team_count"
						:disabled="!form.cond_team"
						class="cond-input"
						placeholder="≥ 人数"
					/>
				</el-form-item>
				<el-form-item label="自己签收单数">
					<el-checkbox v-model="form.cond_self">启用</el-checkbox>
					<el-input
						v-model="form.self_signed_count"
						:disabled="!form.cond_self"
						class="cond-input"
						placeholder="≥ 单"
					/>
				</el-form-item>
				<el-form-item label="邀请用户签收单">
					<el-checkbox v-model="form.cond_team_signed">启用</el-checkbox>
					<el-input
						v-model="form.team_signed_count"
						:disabled="!form.cond_team_signed"
						class="cond-input"
						placeholder="≥ 单"
					/>
				</el-form-item>
				<el-form-item label="累计销售额">
					<el-checkbox v-model="form.cond_sales">启用</el-checkbox>
					<el-input
						v-model="form.total_sales_yuan"
						:disabled="!form.cond_sales"
						class="cond-input"
						placeholder="≥ 元"
					>
						<template slot="append">元</template>
					</el-input>
					<p class="field-tip">提交时按分存储（1 元 = 100 分）。</p>
				</el-form-item>
			</div>

			<el-form-item>
				<el-button type="primary" :loading="saving" @click="onSave">保存</el-button>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
	import { MINI_CONFIG_API, canUseMiniprogram, getApiErrorMessage } from '@/constants/retailPlatform'

	export default {
		name: 'MiniprogramDist',
		inject: ['appdata'],
		data() {
			const percentValidator = (rule, value, callback) => {
				if (value === '' || value == null) {
					callback(new Error('请填写比例'))
					return
				}
				const num = Number(value)
				if (isNaN(num) || num < 0 || num > 100) {
					callback(new Error('需在 0%-100%'))
					return
				}
				callback()
			}
			const intRange = (min, max, label) => {
				return (rule, value, callback) => {
					const num = Number(value)
					if (!Number.isInteger(num) && String(value).indexOf('.') !== -1) {
						callback(new Error(label + '需为整数'))
						return
					}
					if (isNaN(num) || num < min || num > max) {
						callback(new Error(label + '需在 ' + min + '-' + max + ' 之间'))
						return
					}
					callback()
				}
			}
			return {
				pageLoading: false,
				saving: false,
				configured: false,
				form: {
					dist_enabled: 0,
					commission_percent: '50',
					first_percent: '40',
					second_percent: '60',
					dist_apply_team_count: '5',
					dist_settle_days: '15',
					auto_upgrade: false,
					match_mode: 'all',
					cond_team: false,
					team_count: '',
					cond_self: false,
					self_signed_count: '',
					cond_team_signed: false,
					team_signed_count: '',
					cond_sales: false,
					total_sales_yuan: '',
				},
				formRules: {
					commission_percent: [{ validator: percentValidator, trigger: 'blur' }],
					first_percent: [{ validator: percentValidator, trigger: 'blur' }],
					dist_apply_team_count: [{ validator: intRange(1, 100, '邀请人数'), trigger: 'blur' }],
					dist_settle_days: [{ validator: intRange(1, 90, '结算天数'), trigger: 'blur' }],
				},
			}
		},
		mounted() {
			this.loadConfig()
		},
		methods: {
			goAccount() {
				this.$router.push('/StoreConfig/miniprogram/account')
			},
			milliToPercent(val) {
				const num = Number(val)
				if (isNaN(num)) {
					return ''
				}
				return String(num / 10)
			},
			percentToMilli(val) {
				return Math.round(Number(val) * 10)
			},
			isFilledNumber(val) {
				if (val === '' || val == null) {
					return false
				}
				const num = Number(val)
				return !isNaN(num) && num >= 0
			},
			onFirstChange(val) {
				const firstMilli = this.percentToMilli(val)
				if (isNaN(firstMilli)) {
					this.form.second_percent = ''
					return
				}
				this.form.second_percent = this.milliToPercent(1000 - firstMilli)
			},
			parseRules(raw) {
				this.form.auto_upgrade = false
				this.form.match_mode = 'all'
				this.form.cond_team = false
				this.form.team_count = ''
				this.form.cond_self = false
				this.form.self_signed_count = ''
				this.form.cond_team_signed = false
				this.form.team_signed_count = ''
				this.form.cond_sales = false
				this.form.total_sales_yuan = ''
				if (!raw) {
					return
				}
				let parsed = raw
				if (typeof raw === 'string') {
					try {
						parsed = JSON.parse(raw)
					} catch (e) {
						return
					}
				}
				if (!parsed || typeof parsed !== 'object') {
					return
				}
				this.form.auto_upgrade = !!parsed.auto_upgrade
				const rule = (parsed.rules && parsed.rules[0]) || {}
				this.form.match_mode = rule.match_mode === 'any' ? 'any' : 'all'
				const cond = rule.conditions || {}
				if (cond.team_count != null && cond.team_count !== '') {
					this.form.cond_team = true
					this.form.team_count = String(cond.team_count)
				}
				if (cond.self_signed_count != null && cond.self_signed_count !== '') {
					this.form.cond_self = true
					this.form.self_signed_count = String(cond.self_signed_count)
				}
				if (cond.team_signed_count != null && cond.team_signed_count !== '') {
					this.form.cond_team_signed = true
					this.form.team_signed_count = String(cond.team_signed_count)
				}
				if (cond.total_sales != null && cond.total_sales !== '') {
					this.form.cond_sales = true
					this.form.total_sales_yuan = String(Number(cond.total_sales) / 100)
				}
			},
			buildRulesPayload() {
				const conditions = {}
				if (this.form.cond_team) {
					conditions.team_count = Number(this.form.team_count)
				}
				if (this.form.cond_self) {
					conditions.self_signed_count = Number(this.form.self_signed_count)
				}
				if (this.form.cond_team_signed) {
					conditions.team_signed_count = Number(this.form.team_signed_count)
				}
				if (this.form.cond_sales) {
					conditions.total_sales = Math.round(Number(this.form.total_sales_yuan) * 100)
				}
				const payload = {
					auto_upgrade: !!this.form.auto_upgrade,
					rules: [{
						from_level: 1,
						to_level: 2,
						match_mode: this.form.match_mode === 'any' ? 'any' : 'all',
						conditions: conditions,
					}],
				}
				return JSON.stringify(payload)
			},
			loadConfig() {
				this.pageLoading = true
				this.$axios.get(MINI_CONFIG_API.get).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取配置失败'))
						return
					}
					const data = res.data.data || {}
					this.configured = !!data.configured
					if (!this.configured) {
						return
					}
					this.form.dist_enabled = Number(data.dist_enabled) === 1 ? 1 : 0
					this.form.commission_percent = this.milliToPercent(data.dist_commission_rate != null ? data.dist_commission_rate : 500)
					this.form.first_percent = this.milliToPercent(data.dist_first_rate != null ? data.dist_first_rate : 400)
					this.onFirstChange(this.form.first_percent)
					this.form.dist_apply_team_count = String(data.dist_apply_team_count != null ? data.dist_apply_team_count : 5)
					this.form.dist_settle_days = String(data.dist_settle_days != null ? data.dist_settle_days : 15)
					this.parseRules(data.dist_upgrade_rules)
				}).catch((err) => {
					console.log(err)
					this.$message.error('获取配置失败')
				}).finally(() => {
					this.pageLoading = false
				})
			},
			guardPermission() {
				if (!canUseMiniprogram(this.appdata && this.appdata.myinfo)) {
					this.$message.warning('未开通小程序功能，请联系管理员')
					return false
				}
				return true
			},
			onSave() {
				if (!this.guardPermission() || !this.configured) {
					return
				}
				this.$refs.form.validate((valid) => {
					if (!valid) {
						return
					}
					const firstMilli = this.percentToMilli(this.form.first_percent)
					if (isNaN(firstMilli) || firstMilli < 0 || firstMilli > 1000) {
						this.$message.error('一级占比需在 0%-100%')
						return
					}
					const secondMilli = 1000 - firstMilli
					if (this.form.cond_team && !this.isFilledNumber(this.form.team_count)) {
						this.$message.error('请填写邀请人数')
						return
					}
					if (this.form.cond_self && !this.isFilledNumber(this.form.self_signed_count)) {
						this.$message.error('请填写自己签收单数')
						return
					}
					if (this.form.cond_team_signed && !this.isFilledNumber(this.form.team_signed_count)) {
						this.$message.error('请填写邀请用户签收单')
						return
					}
					if (this.form.cond_sales && !this.isFilledNumber(this.form.total_sales_yuan)) {
						this.$message.error('请填写累计销售额')
						return
					}
					const payload = {
						dist_enabled: Number(this.form.dist_enabled) === 1 ? 1 : 0,
						dist_commission_rate: this.percentToMilli(this.form.commission_percent),
						dist_first_rate: firstMilli,
						dist_second_rate: secondMilli,
						dist_apply_team_count: Number(this.form.dist_apply_team_count),
						dist_settle_days: Number(this.form.dist_settle_days),
					}
					this.saving = true
					this.$axios.post(MINI_CONFIG_API.distConfigSave, payload).then((res) => {
						if (!res.data || res.data.code != 0) {
							this.$message.error(getApiErrorMessage(res, '保存失败'))
							return
						}
						return this.$axios.post(MINI_CONFIG_API.distUpgradeSave, {
							upgrade_rules: this.buildRulesPayload(),
						}).then((upgradeRes) => {
							if (!upgradeRes.data || upgradeRes.data.code != 0) {
								this.$message.error(getApiErrorMessage(upgradeRes, '升级规则保存失败'))
								return
							}
							this.$message.success('保存成功')
							this.loadConfig()
						})
					}).catch((err) => {
						console.log(err)
						this.$message.error('保存失败')
					}).finally(() => {
						this.saving = false
					})
				})
			},
		},
	}
</script>

<style scoped lang="less">
	.mini-dist {
		height: 100%;
		background: #fff;
		padding: 16px 20px;
		box-sizing: border-box;
		overflow: auto;
	}

	.guide-card {
		max-width: 520px;
		padding: 24px;
		border: 1px solid #ebeef5;
		border-radius: 4px;
	}

	.guide-title {
		font-size: 16px;
		color: #303133;
		margin-bottom: 8px;
	}

	.guide-desc {
		margin: 0 0 16px;
		font-size: 13px;
		color: #606266;
		line-height: 1.6;
	}

	.setup-form {
		max-width: 640px;
	}

	.form-section {
		margin-bottom: 8px;
		padding-bottom: 8px;
	}

	.section-title {
		font-size: 14px;
		font-weight: 500;
		color: #303133;
		margin: 8px 0 16px;
		padding-left: 8px;
		border-left: 3px solid #ff4714;
	}

	.field-tip {
		margin: 4px 0 0;
		font-size: 12px;
		line-height: 1.4;
		color: #909399;
	}

	.state-text {
		margin-left: 8px;
		font-size: 13px;
		color: #606266;
	}

	.cond-input {
		width: 180px;
		margin-left: 12px;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/miniprogram/dist.vue